import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { Stack, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { addYears, differenceInDays, format } from 'date-fns';
import deLocale from 'date-fns/locale/de';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { OperatingHours } from '../../../services/types/contractDetailsResponse';
import { jhTheme } from '../../../themeProvider';
import { CustomButton } from '../../common/button/customButton';
import {
  getFilterBegin,
  getFilterEnd,
  getTicks,
  linearRegression,
  mapOperatingHours,
} from './usage.controller';

export interface UsageProps {
  prolongedEnd: boolean;
  contractBegin: Date;
  contractEnd: Date;
  operatingHours: OperatingHours[];
  agreedOperatingHours: number;
}

export const Usage = (props: UsageProps) => {
  const { t } = useTranslation();
  const {
    prolongedEnd,
    contractBegin,
    contractEnd,
    operatingHours,
    agreedOperatingHours,
  } = props;

  const filterBeginInit = getFilterBegin(contractBegin);
  const filterEndInit = getFilterEnd(contractEnd, prolongedEnd);

  const [filterBegin, setFilterBegin] = useState(
    filterEndInit < new Date() ? contractBegin : filterBeginInit,
  );
  const [filterEnd, setFilterEnd] = useState(filterEndInit);

  const [mappedOperatingHours, setMappedOperatingHours] = useState(
    mapOperatingHours(operatingHours, filterBegin, filterEnd),
  );

  const [linearRegressionResult, setLinearRegressionResult] = useState(
    mapOperatingHours(operatingHours, filterBegin, filterEnd).length > 1
      ? linearRegression(
          mapOperatingHours(operatingHours, filterBegin, filterEnd).map(
            (r) => r.h,
          ),
          mapOperatingHours(operatingHours, filterBegin, filterEnd).map((e) =>
            e.date.valueOf(),
          ),
        )
      : {
          slope: 0,
          intercept: 0,
          r2: 0,
        },
  );

  const resetFilter = () => {
    setFilterBegin(
      filterEndInit < new Date() ? contractBegin : filterBeginInit,
    );
    setFilterEnd(filterEndInit);
  };

  const handleChangeFilterBegin = (date: Date) => {
    const tmpMappedOperatingHours = mapOperatingHours(
      operatingHours,
      date,
      filterEnd,
    );
    setFilterBegin(date);
    setMappedOperatingHours(tmpMappedOperatingHours);
    if (tmpMappedOperatingHours.length > 1) {
      setLinearRegressionResult(
        linearRegression(
          tmpMappedOperatingHours.map((r) => r.h),
          tmpMappedOperatingHours.map((e) => e.date.valueOf()),
        ),
      );
    }
  };

  const getAgreedOperatingHours = (date: Date) => {
    return Math.round(
      (agreedOperatingHours / 365.25) *
        (differenceInDays(filterBegin, contractBegin) +
          differenceInDays(date, filterBegin)),
    );
  };

  const getProjectedOperatingHours = (date: Date) => {
    const projected = Math.round(
      linearRegressionResult.slope * date.valueOf() +
        linearRegressionResult.intercept,
    );

    if (projected > 0) {
      return projected;
    }
    return undefined;
  };

  const getData = () => {
    let result: {
      date: number;
      usage?: number;
      projected?: number;
      agreed: number;
    }[] = [];

    result.push({
      usage: undefined,
      date: filterBegin.valueOf(),
      projected: undefined,
      agreed: getAgreedOperatingHours(filterBegin),
    });

    result = result.concat(
      mappedOperatingHours.map((mappedOp) => {
        return {
          date: mappedOp.date,
          usage: mappedOp.h,
          projected: getProjectedOperatingHours(new Date(mappedOp.date)),
          agreed: getAgreedOperatingHours(new Date(mappedOp.date)),
        };
      }),
    );

    result.push({
      usage: undefined,
      date: filterEnd.valueOf(),
      projected: getProjectedOperatingHours(filterEnd),
      agreed: getAgreedOperatingHours(filterEnd),
    });

    return result;
  };

  const handleChangeFilterEnd = (date: Date) => {
    const tmpMappedOperatingHours = mapOperatingHours(
      operatingHours,
      filterBegin,
      date,
    );
    setFilterEnd(date);
    setMappedOperatingHours(tmpMappedOperatingHours);
    if (tmpMappedOperatingHours.length > 1) {
      setLinearRegressionResult(
        linearRegression(
          tmpMappedOperatingHours.map((r) => r.h),
          tmpMappedOperatingHours.map((e) => e.date.valueOf()),
        ),
      );
    }
  };

  return (
    <Stack direction="column" spacing={3} sx={{ width: '100%' }}>
      <Stack>
        <Typography variant="headlineDetailSection">
          {t('CurrentUsage')}
        </Typography>
      </Stack>
      <Stack direction="row" spacing={2}>
        <LocalizationProvider
          dateAdapter={AdapterDateFns}
          adapterLocale={deLocale}
        >
          <DatePicker
            minDate={contractBegin}
            maxDate={contractEnd}
            slotProps={{
              textField: {
                className: 'TextField-without-border-radius',
                size: 'small',
                sx: { backgroundColor: jhTheme.palette.background.paper },
              },
            }}
            value={filterBegin}
            onChange={(value) => {
              if (value) {
                handleChangeFilterBegin(value);
              }
            }}
            format={t('DateFormatShort') || ''}
          />

          <DatePicker
            minDate={contractBegin}
            maxDate={addYears(new Date(), 1)}
            slotProps={{
              textField: {
                className: 'TextField-without-border-radius',
                size: 'small',
                sx: { backgroundColor: jhTheme.palette.background.paper },
              },
            }}
            value={filterEnd}
            onChange={(value) => {
              if (value) {
                handleChangeFilterEnd(value);
              }
            }}
            format={t('DateFormatShort') || ''}
          />

          <CustomButton
            handleOnClick={() => {
              resetFilter();
            }}
            text={t('DefaultFilter')}
            sx={{
              fontFamily: 'JHFontBold',
              border: '1px solid transparent',
            }}
            color="primary"
            size="small"
            endIcon={<FilterAltOffIcon />}
          />
        </LocalizationProvider>
      </Stack>
      {contractBegin && contractEnd && (
        <ResponsiveContainer width="100%" height={500}>
          <LineChart data={getData()}>
            <Legend verticalAlign="top" height={30} />
            {new Date() <= filterEnd && new Date() >= filterBegin && (
              <ReferenceLine
                isFront
                strokeWidth={2}
                x={new Date().valueOf()}
                stroke="#000"
                label={t('Today').toString()}
                strokeDasharray="3 3"
              />
            )}
            <Line
              name={t('UsageMeasures')}
              type="linear"
              strokeWidth={1.5}
              dataKey="usage"
              stroke="#000"
            />
            <Tooltip
              labelFormatter={(label) =>
                `${format(new Date(label), 'dd.MM.yyyy')}`
              }
            />
            <Line
              name={t('ContractAgreedOperatingHours')}
              type="linear"
              dataKey="agreed"
              stroke="#21736b"
              strokeWidth={1.5}
              activeDot={false}
              dot={false}
            />

            {(contractEnd > new Date() || prolongedEnd) &&
              mappedOperatingHours.length > 1 && (
                <Line
                  name={t('ProjectedUsage')}
                  type="linear"
                  strokeWidth={1.5}
                  dataKey="projected"
                  connectNulls
                  stroke="#FFB900"
                  activeDot={false}
                  dot={false}
                />
              )}
            <CartesianGrid stroke="#ccc" />
            <XAxis
              scale="linear"
              dataKey="date"
              tickFormatter={(val) => {
                return format(new Date(val), 'MM.yyyy');
              }}
              type="number"
              ticks={getTicks(filterBegin, filterEnd)}
              domain={[
                getTicks(filterBegin, filterEnd)[0],
                getTicks(filterBegin, filterEnd)[
                  getTicks(filterBegin, filterEnd).length - 1
                ],
              ]}
            />
            <YAxis unit="h" />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Stack>
  );
};
